/**
 * Custom hook for managing carousel state and animations
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CAROUSEL_CONFIG } from '../config/brand';

export const useCarousel = (games, hasSelectedGame, setHasSelectedGame) => {
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const carouselRef = useRef(null);
  const ignoreScrollRef = useRef(false);

  /**
   * Converts pixel scroll position to slide index
   */
  const indexFromScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return 0;
    return Math.round(el.scrollLeft / el.offsetWidth);
  }, []);

  /**
   * Handles scroll events with throttling
   */
  const handleScroll = useCallback(() => {
    let ticking = false;
    return () => {
      if (ignoreScrollRef.current || ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const idx = indexFromScroll();
        setCurrentGameIndex((prevIdx) => (prevIdx === idx ? prevIdx : idx));
        ticking = false;
      });
    };
  }, [indexFromScroll])();

  /**
   * Smoothly fades to a specific slide
   */
  const fadeToSlide = useCallback(
    (idx) => {
      const el = carouselRef.current;
      if (!el || idx === currentGameIndex) return;

      setHasSelectedGame(true);
      ignoreScrollRef.current = true;

      // Fade out
      el.style.transition = `opacity ${CAROUSEL_CONFIG.FADE_MS}ms ease-in-out`;
      el.style.opacity = '0';

      // After fade-out completes
      setTimeout(() => {
        // Disable smooth behavior for instant jump
        el.style.scrollBehavior = 'auto';
        el.style.scrollSnapType = 'none';

        // Jump to target slide
        el.scrollTo({ left: idx * el.offsetWidth });
        setCurrentGameIndex(idx);

        // Restore smooth behavior and fade in
        requestAnimationFrame(() => {
          el.style.scrollBehavior = '';
          el.style.scrollSnapType = '';
          el.style.transition = `opacity ${CAROUSEL_CONFIG.FADE_MS}ms ease-in-out`;
          el.style.opacity = '1';

          // Re-enable scroll handling after fade-in
          setTimeout(() => {
            ignoreScrollRef.current = false;
          }, CAROUSEL_CONFIG.FADE_MS);
        });
      }, CAROUSEL_CONFIG.FADE_MS);
    },
    [currentGameIndex, setHasSelectedGame],
  );

  // Auto-rotate games unless user has interacted
  useEffect(() => {
    if (hasSelectedGame) return;

    const intervalId = setInterval(() => {
      fadeToSlide((currentGameIndex + 1) % games.length);
    }, CAROUSEL_CONFIG.AUTO_ROTATE_INTERVAL);

    return () => clearInterval(intervalId);
  }, [currentGameIndex, hasSelectedGame, games.length, fadeToSlide]);

  return {
    currentGameIndex,
    setCurrentGameIndex,
    carouselRef,
    handleScroll,
    fadeToSlide,
  };
};
